import { useRef } from "react";
import { useIntl } from "react-intl";

// Inline-edit primitive. In edit mode every field is a real, always-mounted
// <input>/<textarea> - never a <button> - so keyboard Tab reaches it on every
// platform (macOS drops <button>s from the Tab sequence unless "Keyboard
// navigation" is enabled, which would otherwise make tabbing skip straight from
// one <select> to the next). At rest the control is styled to read as plain
// text (transparent, no ring); on focus it lifts to the active look - white
// fill, ink text, hairline grey ring. `editable=false` renders read-only text
// with no edit affordance.

// `email` / `tel` carry no styling of their own — they exist for the semantic input type and the
// mobile keyboard it selects. Deliberately no `url`: native url validation demands a scheme, so the
// scheme-less "smycles.com" users actually type would read as invalid. Website is a `text` field
// whose shape is checked by `importFieldError` instead.
type Kind = "text" | "date" | "number" | "email" | "tel";

interface InlineEditProps {
  value: string | null | undefined;
  onChange: (next: string) => void;
  // Fires once when focus leaves the field (blur) or on Enter / Cmd+Enter.
  // Receives the committed value. Used to trigger a save without saving on
  // every keystroke. Not fired on Escape (which reverts).
  onCommit?: (committed: string) => void;
  className?: string;
  inputClassName?: string;
  placeholder?: string;
  multiline?: boolean;
  type?: Kind;
  // Numbers for `type="number"`, ISO `YYYY-MM-DD` strings for `type="date"` (a date input's native
  // bounds are date strings, not numbers).
  min?: number | string;
  max?: number | string;
  // Deliberately NO `maxLength`: it silently swallows keystrokes and truncates paste, leaving the user
  // to guess why input stopped. Length is surfaced instead by `importFieldError` + <MergeFieldWarning>
  // and enforced by the save gate — the same mechanism the in-line import review uses.
  editable?: boolean;
  // When false, drops the × clear button and the right-side space it reserves,
  // so the field hugs its content. Use for tightly-spaced inline fields like
  // the given/family name pair, where a per-field clear button is unnecessary
  // and the reserved gap reads as unnaturally wide.
  clearable?: boolean;
  // When true, the field wraps long content onto multiple lines (capped at the
  // container width) instead of growing one line past its bounds. It still hugs
  // its content while it fits, so short values sit inline next to a sibling.
  // Enter commits (no newline). Used for the profile name pair.
  wrap?: boolean;
  // Optional DOM id set on the underlying input/textarea — a stable,
  // locale-independent hook for e2e tests (spark-validation).
  id?: string;
  // Accessible name for the underlying input/textarea. Needed when the field
  // has no visible label of its own (e.g. one side of the LinkedIn merge pair).
  ariaLabel?: string;
}

// Formats a stored value for read-only display. For `type="date"` this turns
// the ISO `YYYY-MM-DD` we store into the German `DD.MM.YYYY` we show. Exported
// so static (non-edit) callsites format dates the same way the editable input
// does — otherwise a read-only date leaks as raw ISO.
export function displayValue(value: string | null | undefined, type: Kind): string | null {
  if (value == null || value === "") return null;
  if (type === "date") {
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) return `${m[3]}.${m[2]}.${m[1]}`;
  }
  return value;
}

export function InlineEdit({
  value,
  onChange,
  onCommit,
  className = "",
  inputClassName = "",
  placeholder = "–",
  multiline = false,
  type = "text",
  min,
  max,
  editable = true,
  clearable = true,
  wrap = false,
  id,
  ariaLabel,
}: InlineEditProps) {
  const intl = useIntl();
  // Captured on focus so Escape can revert to the value the field held when
  // editing began.
  const originalRef = useRef(value);
  // Set by Escape so the synchronous blur that follows commits the REVERTED
  // value: the `value` prop is still the pre-Escape text at that point (React
  // has not re-rendered), and committing that stale text would let a caller
  // act on an edit the user just aborted.
  const escapedRef = useRef(false);

  if (!editable) {
    const display = displayValue(value, type);
    // A multiline value keeps its line breaks in the read-only state — otherwise
    // a locked textarea field (e.g. the profile summary during a LinkedIn merge
    // review) flattens the user's paragraphs into one run-on line.
    if (multiline) {
      return (
        <span className={`block min-w-0 whitespace-pre-wrap break-words ${className}`}>
          {display ? display : <span className="text-ink/30">{placeholder}</span>}
        </span>
      );
    }
    return (
      <span className={`inline-flex min-w-0 items-baseline ${className}`}>
        {display ? display : <span className="text-ink/30">{placeholder}</span>}
      </span>
    );
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !multiline) {
      e.preventDefault();
      onCommit?.(value ?? "");
      e.currentTarget.blur();
    } else if (e.key === "Enter" && multiline && e.metaKey) {
      e.preventDefault();
      onCommit?.(value ?? "");
      e.currentTarget.blur();
    } else if (e.key === "Escape") {
      e.preventDefault();
      escapedRef.current = true;
      onChange(originalRef.current ?? "");
      e.currentTarget.blur();
    }
    // Tab is left to native handling → focus follows DOM order across all fields.
  };

  const onFocus = () => {
    originalRef.current = value;
  };
  const onBlur = () => {
    const committed = escapedRef.current ? (originalRef.current ?? "") : (value ?? "");
    escapedRef.current = false;
    onCommit?.(committed);
  };

  // Resting state reads as plain text (transparent, hairline-less); focus lifts
  // it to the active look - white fill, ink text, grey ring. The grey (not the
  // accent signal colour, which flags mandatory fields) keeps focus neutral.
  const stateCls =
    "bg-transparent text-ink ring-1 ring-transparent placeholder:text-ink/30 transition hover:bg-ink/[0.06] focus:bg-surface focus:text-ink focus:outline-none focus:ring-ink/30";

  // Native number spinners have no place here, and not for taste: the ghost below sizes the field
  // from the TEXT, and the stepper arrows are ~15px of chrome the ghost cannot know about, so a
  // field showing "234" clipped it to "23". Typing and Arrow-Up/Down still work — only the buttons
  // are gone.
  const numberCls =
    type === "number"
      ? "[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
      : "";

  if (multiline) {
    return (
      <textarea
        id={id}
        aria-label={ariaLabel}
        rows={3}
        value={value ?? ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        className={`w-full rounded px-1.5 py-0.5 ${stateCls} ${inputClassName} ${className}`}
      />
    );
  }

  // Wrapping single-logical-line: same ghost trick as the single-line case, but
  // the ghost wraps (whitespace-pre-wrap + break-words) and the span is capped
  // at the container width (max-w-full), so the ghost defines BOTH width (hug
  // while it fits, else full width) and height (1 line, or more when wrapped).
  // The overlaid textarea follows that box. Enter still commits (onKeyDown sees
  // multiline=false), so it behaves as one logical line that merely wraps.
  if (wrap) {
    const wrapGhost = value && value !== "" ? value : placeholder;
    return (
      <span className="group relative -mx-1.5 inline-grid max-w-full grid-cols-[minmax(0,max-content)] align-baseline">
        <span
          aria-hidden="true"
          className={`pointer-events-none invisible col-start-1 row-start-1 whitespace-pre-wrap break-words px-1.5 py-0.5 ${className} ${inputClassName}`}
        >
          {wrapGhost || " "}
        </span>
        <textarea
          id={id}
          aria-label={ariaLabel}
          rows={1}
          cols={1}
          value={value ?? ""}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          className={`col-start-1 row-start-1 h-full w-full resize-none overflow-hidden break-words rounded px-1.5 py-0.5 ${stateCls} ${inputClassName} ${className}`}
        />
      </span>
    );
  }

  // Single-line: size the input to its content via an invisible "ghost" copy of
  // the value (or placeholder when empty), so inline fields (e.g. the centred
  // name) don't snap to the browser's default input width. The input is taken
  // OUT OF FLOW over the ghost (absolute inset-0) rather than sharing grid-area
  // 1/1 with it: an in-flow input contributes its own intrinsic width to the
  // grid column, and for `type="number"` that intrinsic width is the browser's
  // ~170px default — `size={1}` is ignored on anything but text/search/url/tel/
  // email/password — so a two-digit number field rendered a hand-wide gap next
  // to whatever followed it. Out of flow it contributes nothing and the ghost
  // alone decides the box. `-mx-1.5` cancels the input's own padding so the text stays flush
  // with the surrounding label. When the field has a value, the cell reserves
  // ~24px on the right (`pr-6`) so the absolutely-positioned × clear button
  // sits inside the input's visible area without overlapping the text.
  const ghost = value && value !== "" ? value : placeholder;
  const hasValue = Boolean(value && value !== "");
  const showClear = hasValue && clearable;
  const valuePadding = showClear ? "pr-6" : "";
  return (
    <span className="group relative -mx-1.5 inline-grid align-baseline">
      <span
        aria-hidden="true"
        className={`pointer-events-none invisible col-start-1 row-start-1 whitespace-pre px-1.5 py-0.5 ${valuePadding} ${className} ${inputClassName}`}
      >
        {ghost || " "}
      </span>
      <input
        id={id}
        aria-label={ariaLabel}
        type={type}
        size={1}
        min={min}
        max={max}
        value={value ?? ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        className={`absolute inset-0 w-full rounded px-1.5 py-0.5 ${valuePadding} ${stateCls} ${numberCls} ${inputClassName} ${className}`}
      />
      {showClear ? (
        <button
          type="button"
          tabIndex={-1}
          onMouseDown={(e) => {
            // preventDefault stops the click from blurring the input (which
            // would fire onCommit with the OLD value before we clear). We
            // explicitly fire onCommit("") afterwards so the SaveBar still
            // gets a definitive committed value for the cleared field.
            e.preventDefault();
            onChange("");
            onCommit?.("");
          }}
          aria-label={intl.formatMessage({ id: "design.clearValue", defaultMessage: "Clear value" })}
          title={intl.formatMessage({ id: "design.clearValue", defaultMessage: "Clear value" })}
          className="absolute right-1 top-1/2 z-10 flex h-4 w-4 -translate-y-1/2 items-center justify-center rounded-full text-[14px] leading-none text-ink/40 opacity-0 transition group-hover:opacity-100 group-focus-within:opacity-100 hover:bg-ink/10 hover:text-ink"
        >
          × {/* i18n-allow */}
        </button>
      ) : null}
    </span>
  );
}
